import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { getApiErrorMessage } from '@/api/errors';
import { userRoleMap } from '@/config/statusMaps';
import { isValidEmail } from '@/utils/validation';
import type {
  User,
  UserRole,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/types/user';
import './UserModal.css';

/**
 * UserModal component props
 */
export interface UserModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when the modal should close
   */
  onClose: () => void;

  /**
   * Create a user (create mode)
   */
  onCreate: (data: CreateUserRequest) => Promise<User>;

  /**
   * Update a user (edit mode)
   */
  onUpdate: (id: string, data: UpdateUserRequest) => Promise<User>;

  /**
   * User to edit (if editing mode)
   */
  user?: User | null;

  /**
   * Current logged-in user's ID (to prevent self-modifications)
   */
  currentUserId?: string;
}

/**
 * Form validation errors
 */
interface FormErrors {
  email?: string;
  password?: string;
  role?: string;
}

/**
 * Role descriptions for the UI
 */
const roleDescriptions: Record<UserRole, string> = {
  admin: 'Full access to all features including user management',
  operator: 'Can manage nodes, domains, certificates, and NGINX configs',
  readonly: 'View-only access to all resources',
};

/**
 * Roles in display order
 */
const ROLE_OPTIONS: readonly UserRole[] = ['admin', 'operator', 'readonly'];

/**
 * UserModal component for creating and editing users
 *
 * @example
 * ```tsx
 * <UserModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onCreate={createUser}
 *   onUpdate={updateUser}
 *   user={selectedUser}
 * />
 * ```
 */
export function UserModal({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  user,
  currentUserId,
}: UserModalProps) {
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('readonly');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isEditMode = !!user;
  const isSelfEdit = user?.id === currentUserId;

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setEmail(user.email);
        setPassword('');
        setRole(user.role);
        setIsActive(user.is_active);
      } else {
        setEmail('');
        setPassword('');
        setRole('readonly');
        setIsActive(true);
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, user]);

  /**
   * Validate the form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email.trim())) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation (required for new users, optional for edit)
    if (!isEditMode) {
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    } else if (password && password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      if (user) {
        const updateData: UpdateUserRequest = {
          email: email.trim(),
          role,
          is_active: isActive,
          ...(password ? { password } : {}),
        };
        await onUpdate(user.id, updateData);
      } else {
        const createData: CreateUserRequest = {
          email: email.trim(),
          password,
          role,
        };
        await onCreate(createData);
      }
      onClose();
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'Failed to save user'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit User' : 'Add User'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" form="user-form" isLoading={isSaving}>
            {isEditMode ? 'Update User' : 'Create User'}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} className="user-modal-form" noValidate>
        {submitError && (
          <div className="user-modal-error" role="alert">
            {submitError}
          </div>
        )}

        <Input
          label="Email"
          type="email"
          placeholder="Enter email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
          disabled={isSaving}
        />

        <Input
          label={isEditMode ? 'New Password' : 'Password'}
          type="password"
          placeholder={isEditMode ? 'Leave empty to keep current password' : 'Enter password (min 8 characters)'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          required={!isEditMode}
          disabled={isSaving}
          helperText={isEditMode ? 'Leave empty to keep current password' : 'Password must be at least 8 characters'}
        />

        <div className="user-modal-field">
          <span className="user-modal-label">
            Role
            <span className="user-modal-required">*</span>
          </span>
          <div className="user-modal-role-options" role="radiogroup" aria-label="Role">
            {ROLE_OPTIONS.map((option) => (
              <label key={option} className="user-modal-radio">
                <input
                  type="radio"
                  name="role"
                  value={option}
                  checked={role === option}
                  onChange={() => setRole(option)}
                  disabled={isSaving || isSelfEdit}
                />
                <span className="user-modal-radio-label">
                  <strong>{userRoleMap.get(option).label}</strong>
                  <span className="user-modal-radio-description">{roleDescriptions[option]}</span>
                </span>
              </label>
            ))}
          </div>
          {errors.role && (
            <span className="user-modal-error-text">{errors.role}</span>
          )}
          {isSelfEdit && (
            <span className="user-modal-error-text">
              You cannot change your own role
            </span>
          )}
        </div>

        {isEditMode && (
          <div className="user-modal-field">
            <span className="user-modal-label">Status</span>
            <label className="user-modal-toggle">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSaving || isSelfEdit}
              />
              <span className="user-modal-toggle-label">
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </label>
            {isSelfEdit && (
              <span className="user-modal-error-text">
                You cannot deactivate your own account
              </span>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}
